import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BrandIndexService, EducationQueryRequest, EducationQueryResponse } from '../../../../services/brand-index.service';
import { CategoryService } from '../../../../services/category.service';

export interface FilterData {
  selectedEntities: any[];
  category: string;
  country: string;
  models?: string[];
  experience_level?: string;
  budget_range?: string;
}

export interface AnalysisResponse {
  table: any[];
  chart: any;
  evidence_sources: any[];
  universities: string[];
  query: string;
  course: string;
  country: string;
  cached: boolean;
  data_source: string;
  timestamp: string;
  source_badge: string;
  live_badge: string;
  total_sources: number;
  llm_sources: number;
  bing_sources: number;
  metadata: any;
}

export interface TabFlow {
  type: 'education' | 'education-university' | 'brand-analysis' | 'generic';
  activeTab: string;
}

@Injectable({
  providedIn: 'root'
})
export class TemplateCommonService {
  // BehaviorSubjects for reactive data flow
  private filterDataSubject = new BehaviorSubject<FilterData | null>(null);
  private analysisResponseSubject = new BehaviorSubject<AnalysisResponse | null | any>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);
  private tabFlowSubject = new BehaviorSubject<TabFlow>({ type: 'education', activeTab: 'overview' });
  private compareClickedSubject = new BehaviorSubject<boolean>(false);

  // Observables for components to subscribe to
  public filterData$ = this.filterDataSubject.asObservable();
  public analysisResponse$ = this.analysisResponseSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();
  public error$ = this.errorSubject.asObservable();
  public tabFlow$ = this.tabFlowSubject.asObservable();
  public compareClicked$ = this.compareClickedSubject.asObservable();

  constructor(
    private brandIndexService: BrandIndexService,
    private categoryService: CategoryService
  ) {
    // No static initialization - wait for filter changes from common-filter component
  }

  // Methods to update data
  updateFilterData(filterData: FilterData): void {
    // // console.log('TemplateCommonService: Updating filter data:', filterData);
    this.filterDataSubject.next(filterData);
  }

  // Handle filter changes from common-filter component
  handleFilterChange(filterData: { category: string; country: string; selectedEntities: any[] }): void {
    const fullFilterData: FilterData = {
      selectedEntities: filterData.selectedEntities,
      category: filterData.category,
      country: filterData.country,
      models: ['gpt-4', 'claude-3'], // Default models
      experience_level: 'intermediate', // Default experience level
      budget_range: 'premium' // Default budget range
    };

    // console.log('TemplateCommonService: Handling filter change from common-filter:', fullFilterData);
    this.filterDataSubject.next(fullFilterData);
  }

  // Initialize filter data with custom values (useful for components with their own defaults)
  initializeFilterData(selectedEntities: any[], category: string, country: string, additionalData?: Partial<FilterData>): void {
    const filterData: FilterData = {
      selectedEntities,
      category,
      country,
      models: additionalData?.models || ['gpt-4', 'claude-3'],
      experience_level: additionalData?.experience_level || 'intermediate',
      budget_range: additionalData?.budget_range || 'premium'
    };

    // console.log('Initializing filter data with custom values:', filterData);
    this.filterDataSubject.next(filterData);
  }

  updateAnalysisResponse(response: AnalysisResponse): void {
    // // console.log('TemplateCommonService: Updating analysis response:', response);
    this.analysisResponseSubject.next(response);
  }

  setLoading(loading: boolean): void {
    this.isLoadingSubject.next(loading);
  }

  setError(error: string | null): void {
    this.errorSubject.next(error);
  }

  updateTabFlow(tabFlow: TabFlow): void {
    // // console.log('TemplateCommonService: Updating tab flow:', tabFlow);
    this.tabFlowSubject.next(tabFlow);
  }

  // Methods to manage compare state
  setCompareClicked(clicked: boolean): void {
    // // console.log('TemplateCommonService: Setting compare clicked to:', clicked);
    this.compareClickedSubject.next(clicked);
  }

  getCompareClicked(): boolean {
    return this.compareClickedSubject.value;
  }

  // Method to trigger analysis - Now using real API calls
  triggerAnalysis(filterData: FilterData): void {
    // // console.log('TemplateCommonService: Triggering analysis with:', filterData);

    // Validation
    if (filterData.selectedEntities.length === 0) {
      this.setError('Please select at least one entity to analyze');
      return;
    }

    if (!filterData.category || !filterData.country) {
      this.setError('Please select a category and country');
      return;
    }

    this.analysisResponseSubject.next(true);

    // this.setLoading(true);
    // this.setError(null);

    // // Prepare payload for education flow
    // const selectedUniversityNames = filterData.selectedEntities.map(entity => entity.name);
    // const isSingleUniversity = selectedUniversityNames.length === 1;

    // // Create more specific query based on number of universities selected
    // let queryText;
    // if (isSingleUniversity) {
    //   queryText = `Analysis for ${selectedUniversityNames[0]} in ${filterData.category} program in ${filterData.country}`;
    // } else {
    //   queryText = `Best universities for ${filterData.category} in ${filterData.country}`;
    // }

    // const payload: EducationQueryRequest = {
    //   filters: {
    //     course: filterData.category,
    //     country: filterData.country === 'Global' ? null : filterData.country,
    //     models: filterData.models || ['chatgpt'],
    //     universities: selectedUniversityNames,
    //     force_refresh: false,
    //     experience_level: filterData.experience_level || 'intermediate',
    //     budget_range: filterData.budget_range || 'medium'
    //   },
    //   query: queryText
    // };

    // // // console.log('TemplateCommonService: API payload:', payload);
    // // console.log('TemplateCommonService: Single university selected:', isSingleUniversity, 'Universities:', selectedUniversityNames);

    // // Call the API - Updated to use overview analysis endpoint
    // this.brandIndexService.getEducationOverviewAnalysis(payload).subscribe({
    //   next: (response: EducationQueryResponse) => {
    //     // // console.log('TemplateCommonService: API response received:', response);

    //     // Filter response to only include selected universities
    //     const filteredResponse = this.filterResponseBySelectedUniversities(response, selectedUniversityNames);

    //     // Transform response to AnalysisResponse format
    //     // const analysisResponse: AnalysisResponse = {
    //     //   table: filteredResponse.table || [],
    //     //   chart: filteredResponse.chart || null,
    //     //   evidence_sources: filteredResponse.evidence_sources || [],
    //     //   universities: filteredResponse.universities || [],
    //     //   query: filteredResponse.query || '',
    //     //   course: filteredResponse.course || '',
    //     //   country: filteredResponse.country || '',
    //     //   cached: filteredResponse.cached || false,
    //     //   data_source: filteredResponse.data_source || '',
    //     //   timestamp: filteredResponse.timestamp || '',
    //     //   source_badge: filteredResponse.source_badge || '',
    //     //   live_badge: filteredResponse.live_badge || '',
    //     //   total_sources: filteredResponse.total_sources || 0,
    //     //   llm_sources: filteredResponse.llm_sources || 0,
    //     //   bing_sources: filteredResponse.bing_sources || 0,
    //     //   metadata: {}
    //     // };

    //     // this.updateAnalysisResponse(analysisResponse);
    //     this.updateAnalysisResponse(filteredResponse);
    //     this.setLoading(false);
    //   },
    //   error: (err: any) => {
    //     console.error('TemplateCommonService: API error:', err);
    //     this.setError(err?.error?.detail || 'Failed to get analysis results');
    //     this.setLoading(false);
    //   }
    // });
  }

  // Get current values (useful for components that need immediate access)
  getCurrentFilterData(): FilterData | null {
    return this.filterDataSubject.value;
  }

  getCurrentAnalysisResponse(): AnalysisResponse | null {
    return this.analysisResponseSubject.value;
  }

  getCurrentTabFlow(): TabFlow {
    return this.tabFlowSubject.value;
  }

  // Clear data
  clearData(): void {
    this.filterDataSubject.next(null);
    this.analysisResponseSubject.next(null);
    this.setLoading(false);
    this.setError(null);
    this.setCompareClicked(false);
  }

  // Reset filter data (clears current data)
  resetFilterData(): void {
    // console.log('TemplateCommonService: Resetting filter data');
    this.filterDataSubject.next(null);
  }

  // Check if filter data is properly initialized
  isFilterDataInitialized(): boolean {
    const currentData = this.filterDataSubject.value;
    return currentData !== null &&
           currentData.selectedEntities &&
           currentData.selectedEntities.length > 0 &&
           !!currentData.category &&
           !!currentData.country;
  }

  // Filter response to only include selected universities
  private filterResponseBySelectedUniversities(response: any, selectedUniversityNames: string[]): any {
    if (!response || !selectedUniversityNames || selectedUniversityNames.length === 0) {
      return response;
    }

    // console.log('TemplateCommonService: Filtering response for universities:', selectedUniversityNames);
    const filteredResponse = { ...response };

    // Filter table data
    if (filteredResponse.table && Array.isArray(filteredResponse.table)) {
      filteredResponse.table = filteredResponse.table.filter((row: any) =>
        selectedUniversityNames.includes(row.university)
      );
    }

    // Filter chart data
    if (filteredResponse.chart && filteredResponse.chart.series && Array.isArray(filteredResponse.chart.series)) {
      filteredResponse.chart.series = filteredResponse.chart.series.filter((item: any) =>
        selectedUniversityNames.includes(item.name)
      );
    }

    // Filter universities array
    if (filteredResponse.universities && Array.isArray(filteredResponse.universities)) {
      filteredResponse.universities = filteredResponse.universities.filter((uni: string) =>
        selectedUniversityNames.includes(uni)
      );
    }

    // console.log('TemplateCommonService: Filtered response - Table rows:', filteredResponse.table?.length, 'Chart series:', filteredResponse.chart?.series?.length, 'Universities:', filteredResponse.universities?.length);
    return filteredResponse;
  }

  // AI Visibility Index Analysis - Now using real API calls
  loadAIVisibilityIndex(selectedEntities: any[], category: string, forceRefresh: boolean = false): Observable<any> {
    // // console.log('TemplateCommonService: Loading AI Visibility Index with:', { selectedEntities, category, forceRefresh });

    if (!selectedEntities || selectedEntities.length === 0) {
      this.setError('No entities selected for AI Visibility Index analysis');
      return new Observable(subscriber => {
        subscriber.error('No entities selected');
      });
    }

    // Convert universities to string array
    const universities = selectedEntities.map(entity => entity.name);

    const payload = {
      universities: universities,
      course: category,
      include_competitors: true,
      force_refresh: forceRefresh
    };

    // // console.log('TemplateCommonService: AI Visibility Index payload:', payload);

    // Don't set global loading state for tab-specific calls
    // The component will handle its own loading state
    this.setError(null);

    return this.brandIndexService.analyzeAIVisibility(payload).pipe(
      map((response) => {
        // // console.log('TemplateCommonService: AI Visibility Index response:', response);

        // Transform the response to match the expected format
        const transformedData = {
          table: this.transformAIVisibilityToTable(response),
          chart: response.chart_data || null,
          evidence_sources: response.context?.citations || [],
          recommendations: response.context?.recommendations || [],
          citations: response.context?.citations || [],
          source_chips: this.transformCitationsToSourceChips(response.context?.citations || []),
          citation_badge: null,
          ai_visibility_data: response
        };

        // // console.log('TemplateCommonService: Transformed data:', transformedData);
        // // console.log('TemplateCommonService: ai_visibility_data set to:', transformedData.ai_visibility_data);

        return transformedData;
      }),
      catchError((error) => {
        console.error('TemplateCommonService: AI Visibility Index error:', error);
        this.setError('Failed to load AI Visibility Index data');
        throw error;
      })
    );
  }

  // Category Analysis Index - Direct service call like AI Visibility Index
  loadCategoryAnalysisIndex(selectedEntities: any[], category: string, forceRefresh: boolean = false): Observable<any> {
    // console.log('TemplateCommonService: Loading Category Analysis with:', { selectedEntities, category, forceRefresh });

    if (!selectedEntities || selectedEntities.length === 0) {
      this.setError('No entities selected for Category Analysis');
      return new Observable(subscriber => {
        subscriber.error('No entities selected');
      });
    }

    // Convert universities to string array
    const universities = selectedEntities.map(entity => entity.name);

    // Get country from current filter data, fallback to 'UK' if not available
    const currentFilterData = this.getCurrentFilterData();
    const country = currentFilterData?.country || 'UK';

    const payload = {
      universities: universities,
      country: country
    };

    // console.log('TemplateCommonService: Category Analysis payload:', payload);

    // Don't set global loading state for tab-specific calls
    // The component will handle its own loading state
    this.setError(null);

    // Use CategoryService instead of BrandIndexService for category analysis
    const categoryPayload = {
      brands: universities,
      category: category || 'Technology',
      region: country || 'North America',
      include_competitors: true,
      force_refresh: false
    };

    return this.categoryService.analyzeCategory(categoryPayload).pipe(
      map((response) => {
        // console.log('TemplateCommonService: Category Analysis response:', response);

        // Transform the response to match the expected format
        const transformedData = this.transformCategoryAnalysisResponse(response);

        // console.log('TemplateCommonService: Transformed Category Analysis data:', transformedData);

        return transformedData;
      }),
      catchError((error) => {
        console.error('TemplateCommonService: Category Analysis error:', error);

        // Provide more detailed error information
        let errorMessage = 'Failed to load Category Analysis data';
        if (error?.error?.message) {
          errorMessage = `Failed to load Category Analysis data: ${error.error.message}`;
        } else if (error?.message) {
          errorMessage = `Failed to load Category Analysis data: ${error.message}`;
        } else if (error?.status === 404) {
          errorMessage = 'Category Analysis API endpoint not found';
        } else if (error?.status === 500) {
          errorMessage = 'Internal server error while loading Category Analysis data';
        }

        this.setError(errorMessage);
        throw error;
      })
    );
  }

  // Sentiment Tracking Index - Direct service call like AI Visibility Index
  loadSentimentTrackingIndex(selectedEntities: any[], category: string, forceRefresh: boolean = false): Observable<any> {
    // console.log('TemplateCommonService: Loading Sentiment Tracking with:', { selectedEntities, category, forceRefresh });

    if (!selectedEntities || selectedEntities.length === 0) {
      this.setError('No entities selected for Sentiment Tracking');
      return new Observable(subscriber => {
        subscriber.error('No entities selected');
      });
    }

    // Convert universities to string array
    const universities = selectedEntities.map(entity => entity.name);

    // Get country from current filter data, fallback to 'UK' if not available
    const currentFilterData = this.getCurrentFilterData();
    const country = currentFilterData?.country || 'UK';

    // Use the simpler payload structure as per backend documentation
    const payload = {
      universities: universities,
      course: category || 'MBA',
      country: country
    };

    // console.log('TemplateCommonService: Sentiment Tracking payload:', payload);

    // Don't set global loading state for tab-specific calls
    // The component will handle its own loading state
    this.setError(null);

    return this.brandIndexService.getEducationSentimentAnalysis(payload).pipe(
      map((response) => {
        // console.log('TemplateCommonService: Sentiment Tracking response (raw):', response);
        // Return the raw response without transformation
        return response;
      }),
      catchError((error) => {
        console.error('TemplateCommonService: Sentiment Tracking error:', error);
        this.setError('Failed to load Sentiment Tracking data');
        throw error;
      })
    );
  }

  private transformAIVisibilityToTable(response: any): any[] {
    if (!response.universities) return [];

    return response.universities.map((university: any) => ({
      university: university.name,
      ai_visibility_score: university.ai_visibility_score,
      content_quality: university.content_quality,
      digital_presence: university.digital_presence,
      innovation_index: university.innovation_index,
      ai_adoption_rate: university.ai_adoption_rate
    }));
  }

  private transformCitationsToSourceChips(citations: any[]): any[] {
    return citations.map(citation => ({
      source: citation.source,
      url: citation.url,
      title: citation.title,
      confidence: citation.confidence,
      type: citation.type,
      domain: citation.domain,
      category: citation.category || 'ai-visibility'
    }));
  }

  // Transform Category Analysis API response to match expected format
  private transformCategoryAnalysisResponse(response: any): any {
    if (!response) {
      return {
        table: [],
        chart: null,
        evidence_sources: [],
        recommendations: [],
        citations: [],
        source_chips: [],
        citation_badge: null
      };
    }

    // Handle the actual API response structure (from category.service.ts interface)
    // The response should have: composite_score, ai_category_insights, ai_table_summary, etc.

    // Transform table data from ai_table_summary
    const tableData = response.ai_table_summary ?
      response.ai_table_summary.rows.map((row: any[], index: number) => ({
        id: index,
        category: row[0],
        score: row[1]
      })) : [];

    // Transform chart data from ai_category_insights
    const chartData = response.ai_category_insights ? {
      title: response.ai_category_insights.title,
      type: response.ai_category_insights.type,
      x_axis: response.ai_category_insights.x_axis,
      series: response.ai_category_insights.series,
      data: response.ai_category_insights.x_axis.map((category: string, index: number) => ({
        name: category,
        value: response.ai_category_insights.series[0].data[index]
      }))
    } : null;

    // Transform evidence sources and citations
    const evidenceSources = response.citations || [];
    const sourceChips = this.transformCitationsToSourceChips(evidenceSources);

    // Transform recommendations
    const recommendations = response.recommendations ?
      [...(response.recommendations.strengths || []), ...(response.recommendations.weaknesses || [])] : [];

    return {
      table: tableData,
      chart: chartData,
      evidence_sources: evidenceSources,
      recommendations: recommendations,
      citations: evidenceSources,
      source_chips: sourceChips,
      citation_badge: null,
      // Add the original response for components that need the full structure
      composite_score: response.composite_score,
      ai_category_insights: response.ai_category_insights,
      ai_table_summary: response.ai_table_summary,
      category_demand: response.category_demand,
      subject_demand_analysis: response.subject_demand_analysis,
      university_info: response.university_info,
      original_recommendations: response.recommendations,
      original_citations: response.citations,
      context: response.context,
      metadata: response.metadata
    };
  }



  // Transform category analysis data to table format for display
  private transformCategoryAnalysisToTable(categoryAnalysis: any[]): any[] {
    if (!categoryAnalysis || categoryAnalysis.length === 0) {
      return [];
    }

    const tableData: any[] = [];

    categoryAnalysis.forEach(category => {
      category.universities.forEach((university: any) => {
        tableData.push({
          category: category.category,
          university: university.name,
          score: university.score,
          world_ranking: university.rankings?.world_ranking || 'N/A',
          category_ranking: university.rankings?.category_ranking || 'N/A',
          employment_rate: university.employment_data?.employment_rate || 'N/A',
          average_salary: university.employment_data?.average_salary || 'N/A',
          domestic_fees: university.fees?.domestic || 'N/A',
          international_fees: university.fees?.international || 'N/A',
          course_offerings: university.course_offerings?.join(', ') || 'N/A',
          strengths: university.strengths?.join(', ') || 'N/A',
          weaknesses: university.weaknesses?.join(', ') || 'N/A'
        });
      });
    });

    return tableData;
  }

  // ===== EDUCATION TAB-SPECIFIC METHODS =====

  // Transform area comparison response to unified format
  transformAreaComparisonResponse(response: any): any {
    if (!response) return null;

    // console.log('Transforming area comparison response:', response);

    // Handle the new API structure from the selected file
    if (response.universities && Array.isArray(response.universities)) {
      // New structure: universities array with scores object
      const comparisonData = response.universities.map((university: any) => {
        const transformedUni: any = {
          name: university.name || 'Unknown University'
        };

        // Extract metrics from scores object
        if (university.scores && typeof university.scores === 'object') {
          Object.keys(university.scores).forEach(metricName => {
            const metricValue = university.scores[metricName];
            if (typeof metricValue === 'number') {
              transformedUni[metricName] = metricValue;
            }
          });
        }

        return transformedUni;
      });

      return {
        table: comparisonData,
        chart: response.chart_data || null,
        comparison_data: comparisonData,
        universities: response.universities, // Keep original for component access
        evidence_sources: response.evidence_sources || [],
        recommendations: response.recommendations || [],
        citations: response.citations || [],
        source_chips: response.source_chips || [],
        citation_badge: response.citation_badge || null,
        comparison_summary: response.comparison_summary || null,
        context: response.context || null
      };
    }

    // Handle chart_data structure (fallback for old format)
    if (response.chart_data && response.chart_data.data && Array.isArray(response.chart_data.data)) {
      const comparisonData = response.chart_data.data.map((university: any) => {
        const transformedUni: any = {
          name: university.name || 'Unknown University'
        };

        // Extract metrics from series
        if (university.series && Array.isArray(university.series)) {
          university.series.forEach((metric: any) => {
            if (metric.name && metric.value !== undefined) {
              transformedUni[metric.name] = metric.value;
            }
          });
        }

        return transformedUni;
      });

      return {
        table: comparisonData,
        chart: response.chart_data,
        comparison_data: comparisonData,
        evidence_sources: response.evidence_sources || [],
        recommendations: response.recommendations || [],
        citations: response.citations || [],
        source_chips: response.source_chips || [],
        citation_badge: response.citation_badge || null
      };
    }

    // Handle legacy data structure
    let chartData = null;
    let evidenceSources = [];
    let recommendations = [];

    // Extract evidence sources and recommendations
    if (response.evidence_sources) {
      evidenceSources = response.evidence_sources;
    }
    if (response.recommendations) {
      recommendations = response.recommendations;
    }

    // Handle the area-comparison API response structure
    if (response.data && Array.isArray(response.data)) {
      // Find the first data item with chart_data
      for (const dataItem of response.data) {
        if (dataItem.scores && dataItem.scores.chart_data) {
          chartData = dataItem.scores.chart_data;
          break;
        }
      }
    }

    // Transform chart data to the format expected by Score Cards
    let comparisonData = [];
    if (chartData && Array.isArray(chartData)) {
      comparisonData = chartData.map((university: any) => {
        const transformedUni: any = {
          name: university.name || university.university || 'Unknown University'
        };

        // Extract metrics from series
        if (university.series && Array.isArray(university.series)) {
          university.series.forEach((metric: any) => {
            if (metric.name && metric.value !== undefined) {
              transformedUni[metric.name] = metric.value;
            }
          });
        }

        return transformedUni;
      });
    }

    return {
      table: comparisonData,
      chart: chartData,
      comparison_data: comparisonData,
      evidence_sources: evidenceSources,
      recommendations: recommendations,
      citations: response.citations || [],
      source_chips: response.source_chips || [],
      citation_badge: response.citation_badge || null
    };
  }

  // Transform AI content index response
  transformAIContentIndexResponse(response: any): any {
    // // console.log('TemplateCommonService: Transforming AI content index response:', response);

    // Extract universities data
    const universities = response.universities || [];
    const chartData = response.chart_data || {};
    const contentSummary = response.content_summary || {};
    const recommendations = response.recommendations || [];
    const context = response.context || {};
    const evidenceSources = response.evidence_sources || [];

    return {
      universities: universities,
      chart_data: chartData,
      content_summary: contentSummary,
      recommendations: recommendations,
      context: context,
      evidence_sources: evidenceSources,
      cached: response.cached || false,
      timestamp: response.timestamp,
      models_used: response.models_used || [],
      plan_type: response.plan_type,
      metadata: response.metadata || {}
    };
  }

  // Transform content creation response
  transformContentCreationResponse(response: any): any {
    // // console.log('TemplateCommonService: Transforming content creation response:', response);

    // Handle new platform-based response format
    if (response.platform_analysis) {
      return {
        // Transform platform analysis to match expected format
        platform_analysis: response.platform_analysis,
        content_recommendations: response.content_recommendations || [],
        evidence_sources: response.evidence_sources || [],
        recommendations: response.content_recommendations || [],
        university: response.university,
        timestamp: response.timestamp,
        model_meta: response.model_meta || {},
        // Legacy format compatibility
        universities: [{
          name: response.university,
          platform_analysis: response.platform_analysis,
          content_recommendations: response.content_recommendations
        }],
        content_summary: {
          platform_count: Object.keys(response.platform_analysis || {}).length,
          total_recommendations: (response.content_recommendations || []).length
        },
        recommendations_summary: {
          total_recommendations: (response.content_recommendations || []).length,
          by_priority: {
            high: (response.content_recommendations || []).filter((r: any) => r.impact_score >= 80).length,
            medium: (response.content_recommendations || []).filter((r: any) => r.impact_score >= 60 && r.impact_score < 80).length,
            low: (response.content_recommendations || []).filter((r: any) => r.impact_score < 60).length
          }
        },
        context: {
          title: "Content Creation Analysis",
          summary: `Platform-specific content analysis for ${response.university}`,
          methodology: "Analysis based on platform-specific content needs and improvement opportunities"
        }
      };
    }

    // Fallback to old format if response doesn't have platform_analysis
    const universities = response.universities || [];
    const contentSummary = response.content_summary || {};
    const recommendationsSummary = response.recommendations_summary || {};
    const context = response.context || {};
    const evidenceSources = response.evidence_sources || [];

    return {
      universities: universities,
      content_summary: contentSummary,
      recommendations_summary: recommendationsSummary,
      context: context,
      evidence_sources: evidenceSources,
      cached: response.cached || false,
      timestamp: response.timestamp,
      models_used: response.models_used || [],
      plan_type: response.plan_type,
      metadata: response.metadata || {}
    };
  }

  // Load AI content index data for education
  loadAIContentIndex(selectedEntities: any[], category: string, forceRefresh: boolean = false): Observable<any> {
    // // console.log('TemplateCommonService: Loading AI content index for:', selectedEntities, category, forceRefresh);

    // Get country from current filter data, fallback to 'UK' if not available
    const currentFilterData = this.getCurrentFilterData();
    const country = currentFilterData?.country || 'UK';

    const payload = {
      universities: selectedEntities.map(entity => entity.name),
      course: category,
      country: country
    };

    return this.brandIndexService.getEducationAIContentIndex(payload)
      .pipe(
        map(response => this.transformAIContentIndexResponse(response))
      );
  }

  // Load content creation data for education
  loadContentCreation(selectedEntities: any[], category: string, forceRefresh: boolean = false): Observable<any> {
    // // console.log('TemplateCommonService: Loading content creation for:', selectedEntities, category, forceRefresh);

    // Get country from current filter data, fallback to 'UK' if not available
    const currentFilterData = this.getCurrentFilterData();
    const country = currentFilterData?.country || 'UK';

    const payload = {
      universities: selectedEntities.map(entity => entity.name),
      course: category,
      country: country
    };

    return this.brandIndexService.getEducationContentCreation(payload)
      .pipe(
        map(response => this.transformContentCreationResponse(response))
      );
  }

  // Load area comparison data for education
  loadAreaComparisonIndex(selectedEntities: any[], category: string, forceRefresh: boolean = false): Observable<any> {
    // // console.log('TemplateCommonService: Loading area comparison index for:', selectedEntities, category, forceRefresh);

    // Get country from current filter data, fallback to 'UK' if not available
    const currentFilterData = this.getCurrentFilterData();
    const country = currentFilterData?.country || 'UK';

    const payload = {
      universities: selectedEntities.map(entity => entity.name),
      course: category,
      country: country
    };

    return this.brandIndexService.getEducationAreaComparison(payload)
      .pipe(
        map(response => this.transformAreaComparisonResponse(response))
      );
  }

  // Load data for specific education tabs
  loadEducationTabData(tabId: string, filterData: FilterData): Observable<any> {
    // // console.log('TemplateCommonService: Loading education tab data for:', tabId, filterData);

    // Prepare payload for education flow
    const selectedUniversityNames = filterData.selectedEntities.map(entity => entity.name);
    const isSingleUniversity = selectedUniversityNames.length === 1;

    // Create more specific query based on number of universities selected
    let queryText;
    if (isSingleUniversity) {
      queryText = `Analysis for ${selectedUniversityNames[0]} in ${filterData.category} program in ${filterData.country}`;
    } else {
      queryText = `Analysis for ${filterData.category} in ${filterData.country}`;
    }

    const payload: EducationQueryRequest = {
      filters: {
        course: filterData.category,
        country: filterData.country === 'Global' ? null : filterData.country,
        models: filterData.models || ['chatgpt'],
        universities: selectedUniversityNames,
        force_refresh: false,
        experience_level: filterData.experience_level || 'intermediate',
        budget_range: filterData.budget_range || 'medium'
      },
      query: queryText
    };

    // For category analysis, we need to transform the payload to match the new Category Analysis API
    if (tabId === 'category-analysis') {
      const categoryAnalysisPayload = {
        universities: filterData.selectedEntities.map(entity => entity.name),
        country: filterData.country === 'Global' ? undefined : filterData.country
      };
      return this.brandIndexService.getEducationCategoryAnalysis(categoryAnalysisPayload)
        .pipe(
          map(response => this.transformCategoryAnalysisResponse(response))
        );
    }

    // Route to appropriate API method based on tab ID
    switch (tabId) {
      case 'overview':
        return this.brandIndexService.getEducationOverviewAnalysis(payload);

      case 'sentiment-tracking':
        return this.brandIndexService.getEducationSentimentAnalysis(payload);

      case 'area-comparison':
        // Transform payload for area comparison API - new structure
        const areaComparisonPayload = {
          universities: filterData.selectedEntities.map(entity => entity.name),
          course: filterData.category,
          country: filterData.country === 'Global' ? 'UK' : filterData.country
        };
        return this.brandIndexService.getEducationAreaComparison(areaComparisonPayload)
          .pipe(
            map(response => this.transformAreaComparisonResponse(response))
          );

      case 'key-influence-sources':
        return this.brandIndexService.getEducationKeyInfluenceSources(payload);

      case 'ai-content-index':
        // Transform payload for AI content index API - new structure
        const aiContentIndexPayload = {
          universities: filterData.selectedEntities.map(entity => entity.name),
          course: filterData.category,
          country: filterData.country === 'Global' ? 'UK' : filterData.country
        };
        return this.brandIndexService.getEducationAIContentIndex(aiContentIndexPayload)
          .pipe(
            map(response => this.transformAIContentIndexResponse(response))
          );

      case 'content-creation':
        // Transform payload for content creation API - simplified structure
        const contentCreationPayload = {
          universities: filterData.selectedEntities.map(entity => entity.name)
        };
        return this.brandIndexService.getEducationContentCreation(contentCreationPayload)
          .pipe(
            map(response => this.transformContentCreationResponse(response))
          );

      case 'performance-tracking':
        return this.brandIndexService.getEducationPerformanceTracking(payload);

      case 'student-comparison':
        return this.brandIndexService.getEducationStudentComparison(payload);

      default:
        // For unknown tabs, return the main education query
        return this.brandIndexService.educationQuery(payload);
    }
  }

  // NEW: Load Our Methodology Analysis
  loadOurMethodologyAnalysis(selectedEntities: any[], category: string, forceRefresh: boolean = false): Observable<any> {
    // console.log('TemplateCommonService: Loading Our Methodology Analysis with:', { selectedEntities, category, forceRefresh });

    if (!selectedEntities || selectedEntities.length === 0) {
      this.setError('No entities selected for Our Methodology Analysis');
      return new Observable(subscriber => {
        subscriber.error('No entities selected');
      });
    }

    // Get country from current filter data, fallback to 'UK' if not available
    const currentFilterData = this.getCurrentFilterData();
    const country = currentFilterData?.country || 'UK';

    const payload = {
      brands: selectedEntities.map(entity => entity.name),
      category: category,
      country: country,
      models: ['gpt-4o-mini'],
      force_refresh: forceRefresh
    };

    // console.log('TemplateCommonService: Our Methodology payload:', payload);
    this.setError(null);

    return this.brandIndexService.getOurMethodologyAnalysis(payload).pipe(
      map((response) => {
        // console.log('TemplateCommonService: Our Methodology response:', response);
        return this.transformOurMethodologyResponse(response);
      }),
      catchError((error) => {
        console.error('TemplateCommonService: Our Methodology error:', error);
        this.setError('Failed to load Our Methodology data');
        throw error;
      })
    );
  }

  // NEW: Load Performance Tracking Analysis
  loadPerformanceTrackingAnalysis(selectedEntities: any[], category: string, forceRefresh: boolean = false): Observable<any> {
    // console.log('TemplateCommonService: Loading Performance Tracking Analysis with:', { selectedEntities, category, forceRefresh });

    if (!selectedEntities || selectedEntities.length === 0) {
      this.setError('No entities selected for Performance Tracking Analysis');
      return new Observable(subscriber => {
        subscriber.error('No entities selected');
      });
    }

    // Get country from current filter data, fallback to 'UK' if not available
    const currentFilterData = this.getCurrentFilterData();
    const country = currentFilterData?.country || 'UK';

    const payload = {
      brands: selectedEntities.map(entity => entity.name),
      category: category,
      country: country,
      models: ['gpt-4o-mini'],
      force_refresh: forceRefresh
    };

    // console.log('TemplateCommonService: Performance Tracking payload:', payload);
    this.setError(null);

    return this.brandIndexService.getPerformanceTrackingAnalysis(payload).pipe(
      map((response) => {
        // console.log('TemplateCommonService: Performance Tracking response:', response);
        return this.transformPerformanceTrackingResponse(response);
      }),
      catchError((error) => {
        console.error('TemplateCommonService: Performance Tracking error:', error);
        this.setError('Failed to load Performance Tracking data');
        throw error;
      })
    );
  }

  // NEW: Load Key Influence Sources Analysis
  loadKeyInfluenceSourcesAnalysis(selectedEntities: any[], category: string, forceRefresh: boolean = false): Observable<any> {
    // console.log('TemplateCommonService: Loading Key Influence Sources Analysis with:', { selectedEntities, category, forceRefresh });

    if (!selectedEntities || selectedEntities.length === 0) {
      this.setError('No entities selected for Key Influence Sources Analysis');
      return new Observable(subscriber => {
        subscriber.error('No entities selected');
      });
    }

    // Get country from current filter data, fallback to 'UK' if not available
    const currentFilterData = this.getCurrentFilterData();
    const country = currentFilterData?.country || 'UK';

    const payload = {
      brands: selectedEntities.map(entity => entity.name),
      category: category,
      country: country,
      models: ['gpt-4o-mini'],
      force_refresh: forceRefresh
    };

    // console.log('TemplateCommonService: Key Influence Sources payload:', payload);
    this.setError(null);

    return this.brandIndexService.getKeyInfluenceSourcesAnalysis(payload).pipe(
      map((response) => {
        // console.log('TemplateCommonService: Key Influence Sources response:', response);
        return this.transformKeyInfluenceSourcesResponse(response);
      }),
      catchError((error) => {
        console.error('TemplateCommonService: Key Influence Sources error:', error);
        this.setError('Failed to load Key Influence Sources data');
        throw error;
      })
    );
  }

  // NEW: Load Consumer Insights Analysis
  loadConsumerInsightsAnalysis(selectedEntities: any[], category: string, forceRefresh: boolean = false): Observable<any> {
    // console.log('TemplateCommonService: Loading Consumer Insights Analysis with:', { selectedEntities, category, forceRefresh });

    if (!selectedEntities || selectedEntities.length === 0) {
      this.setError('No entities selected for Consumer Insights Analysis');
      return new Observable(subscriber => {
        subscriber.error('No entities selected');
      });
    }

    // Get country from current filter data, fallback to 'UK' if not available
    const currentFilterData = this.getCurrentFilterData();
    const country = currentFilterData?.country || 'UK';

    const payload = {
      universities: selectedEntities.map(entity => entity.name),
      course: category,
      country: country,
      include_student_data: true,
      include_enrollment_trends: true,
      include_satisfaction_metrics: true,
      include_career_outcomes: true,
      time_period: '2024-2025',
      models: ['gpt-4o-mini'],
      force_refresh: forceRefresh
    };

    // console.log('TemplateCommonService: Consumer Insights payload:', payload);
    this.setError(null);

    return this.brandIndexService.getConsumerInsightsAnalysis(payload).pipe(
      map((response) => {
        // console.log('TemplateCommonService: Consumer Insights response:', response);
        return this.transformConsumerInsightsResponse(response);
      }),
      catchError((error) => {
        console.error('TemplateCommonService: Consumer Insights error:', error);
        this.setError('Failed to load Consumer Insights data');
        throw error;
      })
    );
  }

  // NEW: Transform Our Methodology Response
  private transformOurMethodologyResponse(response: any): any {
    if (!response) {
      return {
        table: [],
        chart: null,
        evidence_sources: [],
        recommendations: [],
        citations: [],
        source_chips: [],
        citation_badge: null
      };
    }

    const tableData = this.transformOurMethodologyToTable(response);
    const evidenceSources = response.evidence_sources || [];
    const sourceChips = this.transformCitationsToSourceChips(evidenceSources);

    return {
      table: tableData,
      chart: response.chart_data || null,
      evidence_sources: evidenceSources,
      recommendations: response.recommendations || [],
      citations: evidenceSources,
      source_chips: sourceChips,
      citation_badge: null,
      methodology_data: response
    };
  }

  // NEW: Transform Performance Tracking Response
  private transformPerformanceTrackingResponse(response: any): any {
    if (!response) {
      return {
        table: [],
        chart: null,
        evidence_sources: [],
        recommendations: [],
        citations: [],
        source_chips: [],
        citation_badge: null
      };
    }

    const tableData = this.transformPerformanceTrackingToTable(response);
    const evidenceSources = response.evidence_sources || [];
    const sourceChips = this.transformCitationsToSourceChips(evidenceSources);

    return {
      table: tableData,
      chart: response.chart_data || null,
      evidence_sources: evidenceSources,
      recommendations: response.recommendations || [],
      citations: evidenceSources,
      source_chips: sourceChips,
      citation_badge: null,
      performance_tracking_data: response
    };
  }

  // NEW: Transform Key Influence Sources Response
  private transformKeyInfluenceSourcesResponse(response: any): any {
    if (!response) {
      return {
        table: [],
        chart: null,
        evidence_sources: [],
        recommendations: [],
        citations: [],
        source_chips: [],
        citation_badge: null
      };
    }

    const tableData = this.transformKeyInfluenceSourcesToTable(response);
    const evidenceSources = response.evidence_sources || [];
    const sourceChips = this.transformCitationsToSourceChips(evidenceSources);

    return {
      table: tableData,
      chart: response.chart_data || null,
      evidence_sources: evidenceSources,
      recommendations: response.recommendations || [],
      citations: evidenceSources,
      source_chips: sourceChips,
      citation_badge: null,
      key_influence_sources_data: response
    };
  }

  // NEW: Transform Consumer Insights Response
  private transformConsumerInsightsResponse(response: any): any {
    if (!response) {
      return {
        table: [],
        chart: null,
        evidence_sources: [],
        recommendations: [],
        citations: [],
        source_chips: [],
        citation_badge: null
      };
    }

    const tableData = this.transformConsumerInsightsToTable(response);
    const evidenceSources = response.evidence_sources || [];
    const sourceChips = this.transformCitationsToSourceChips(evidenceSources);

    return {
      table: tableData,
      chart: response.chart_data || null,
      evidence_sources: evidenceSources,
      recommendations: response.recommendations || [],
      citations: evidenceSources,
      source_chips: sourceChips,
      citation_badge: null,
      consumer_insights_data: response
    };
  }

  // NEW: Transform Our Methodology data to table format
  private transformOurMethodologyToTable(response: any): any[] {
    if (!response || !response.data) return [];

    const tableData: any[] = [];

    response.data.forEach((item: any) => {
      item.series.forEach((series: any) => {
        tableData.push({
          component: item.name,
          metric: series.name,
          value: series.value,
          description: response.methodology_components?.find((comp: any) => comp.component === item.name)?.description || 'N/A'
        });
      });
    });

    return tableData;
  }

  // NEW: Transform Performance Tracking data to table format
  private transformPerformanceTrackingToTable(response: any): any[] {
    if (!response || !response.universities) return [];

    const tableData: any[] = [];

    response.universities.forEach((university: any) => {
      if (university.performance_metrics) {
        Object.entries(university.performance_metrics).forEach(([metric, data]: [string, any]) => {
          tableData.push({
            university: university.name,
            metric: metric.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            score: data.score,
            rank: data.rank,
            trend: data.trend,
            metrics: data.metrics?.join(', ') || 'N/A'
          });
        });
      }
    });

    return tableData;
  }

  // NEW: Transform Key Influence Sources data to table format
  private transformKeyInfluenceSourcesToTable(response: any): any[] {
    if (!response || !response.universities) return [];

    const tableData: any[] = [];

    response.universities.forEach((university: any) => {
      if (university.influence_sources) {
        university.influence_sources.forEach((source: any) => {
          tableData.push({
            university: university.name,
            domain: source.domain,
            influence_score: source.influence_score,
            citation_count: source.citation_count,
            social_media_presence: source.social_media_presence,
            research_impact: source.research_impact,
            industry_collaborations: source.industry_collaborations,
            description: source.description
          });
        });
      }
    });

    return tableData;
  }

  // NEW: Transform Enhanced Consumer Insights data to table format
  private transformConsumerInsightsToTable(response: any): any[] {
    if (!response || !response.universities) return [];

    const tableData: any[] = [];

    response.universities.forEach((university: any) => {
      // Add enrollment data
      if (university.student_insights?.enrollment_data) {
        const enrollment = university.student_insights.enrollment_data;
        tableData.push({
          university: university.name,
          metric: 'Total Applications',
          value: enrollment.total_applications || 0,
          category: 'Enrollment'
        });
        tableData.push({
          university: university.name,
          metric: 'Admitted',
          value: enrollment.admitted || 0,
          category: 'Enrollment'
        });
        tableData.push({
          university: university.name,
          metric: 'Enrolled',
          value: enrollment.enrolled || 0,
          category: 'Enrollment'
        });
        tableData.push({
          university: university.name,
          metric: 'Yield Rate (%)',
          value: enrollment.yield_rate || 0,
          category: 'Enrollment'
        });
      }

      // Add satisfaction metrics
      if (university.student_insights?.satisfaction_metrics) {
        const satisfaction = university.student_insights.satisfaction_metrics;
        tableData.push({
          university: university.name,
          metric: 'Overall Satisfaction',
          value: satisfaction.overall_satisfaction || 0,
          category: 'Satisfaction'
        });
        tableData.push({
          university: university.name,
          metric: 'Teaching Quality',
          value: satisfaction.teaching_quality || 0,
          category: 'Satisfaction'
        });
        tableData.push({
          university: university.name,
          metric: 'Facilities Rating',
          value: satisfaction.facilities_rating || 0,
          category: 'Satisfaction'
        });
        tableData.push({
          university: university.name,
          metric: 'Campus Life',
          value: satisfaction.campus_life || 0,
          category: 'Satisfaction'
        });
        tableData.push({
          university: university.name,
          metric: 'Retention Rate (%)',
          value: satisfaction.retention_rate || 0,
          category: 'Satisfaction'
        });
      }

      // Add career outcomes
      if (university.student_insights?.career_outcomes) {
        const career = university.student_insights.career_outcomes;
        tableData.push({
          university: university.name,
          metric: 'Placement Rate (%)',
          value: career.placement_rate_6months || 0,
          category: 'Career'
        });
        tableData.push({
          university: university.name,
          metric: 'Average Salary (£)',
          value: career.average_salary || 0,
          category: 'Career'
        });
        tableData.push({
          university: university.name,
          metric: 'Top Industries',
          value: career.top_industries?.join(', ') || 'N/A',
          category: 'Career'
        });
        tableData.push({
          university: university.name,
          metric: 'Top Companies',
          value: career.top_companies?.join(', ') || 'N/A',
          category: 'Career'
        });
      }

      // Add demographics
      if (university.student_insights?.student_demographics) {
        const demographics = university.student_insights.student_demographics;
        tableData.push({
          university: university.name,
          metric: 'Average Age',
          value: demographics.average_age || 0,
          category: 'Demographics'
        });
        tableData.push({
          university: university.name,
          metric: 'Work Experience (Years)',
          value: demographics.work_experience_years || 0,
          category: 'Demographics'
        });
      }
    });

    return tableData;
  }
}
